import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { AccountCircle, Key, Email, Error } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import CustomButton from "./default/CustomButton";
import axios from "axios";
import { setAuthTokens, setAccount } from "../store/slices/auth";

interface IFormProps {
    method: "login" | "register";
    route: string;
}

interface IFormError {
    detail?: string;
    username?: string;
    email?: string;
    password?: string;
}

const Form: React.FC<IFormProps> = ({ method, route }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [username, setUsername] = useState("");
    const [error, setError] = useState<IFormError>({});
    const [loading, setLoading] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAgreedToTerms(event.target.checked);
    };

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const submitButtonName = useMemo(() => (method === "login" ? "Continue" : "Register"), [method]);
    const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value), []);
    const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value), []);
    const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value), []);

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            if (loading) return;
            setLoading(true);

            axios
                .post(`${process.env.REACT_APP_API_URL}${route}`, { email, password, username })
                .then((res) => {
                    dispatch(
                        setAuthTokens({
                            token: res.data.body.access,
                            refreshToken: res.data.body.refresh,
                        })
                    );
                    dispatch(
                        setAccount({
                            username: res.data.body.user,
                            email: res.data.body.email,
                            image: res.data.body.image ? res.data.body.image.url : null,
                        })
                    );
                    navigate("/");
                })
                .catch((error) => {
                    console.log(error);
                    setError({
                        detail: error.response.data.body,
                        email: email,
                        username: username,
                        password: password,
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [dispatch, email, password, username, route, navigate, loading]
    );

    const renderField = (
        id: string,
        label: string,
        type: string,
        Icon: React.ElementType,
        value: string,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
        errorMessage?: string
    ) => (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
            }}
        >
            <Icon
                sx={{
                    color: "action.active",
                    mr: 2,
                    my: 0,
                }}
            />
            <TextField
                error={!!errorMessage}
                id={id}
                label={label}
                type={type}
                variant="standard"
                sx={{
                    width: "200px",
                }}
                value={value}
                onChange={onChange}
            />
            <Box
                sx={{
                    height: "20px",
                    width: "20px",
                    ml: 2,
                    my: 0,
                    position: "relative",
                    float: "right",
                }}
            >
                {error.detail && (
                    <Error
                        sx={{
                            color: "red",
                        }}
                    />
                )}
            </Box>
        </Box>
    );

    const renderToSPrivacyLabel = () => (
        <FormControlLabel
            control={<Checkbox checked={agreedToTerms} onChange={handleCheckboxChange} name="agreeToTerms" />}
            sx={{ my: 4 }}
            label={
                <Typography variant="body2" color="text.secondary">
                    I agree to the{" "}
                    <Typography component="a" href="/tos" variant="body2" color="primary">
                        Terms of Service
                    </Typography>{" "}
                    and{" "}
                    <Typography component="a" href="/privacy" variant="body2" color="primary">
                        Privacy Policy
                    </Typography>
                    .
                </Typography>
            }
        />
    );

    return (
        <Box
            sx={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
            }}
            component="form"
            onSubmit={handleSubmit}
        >
            {error.detail && (
                <Typography
                    sx={{
                        color: "red",
                        mb: 2,
                    }}
                >
                    {error.detail}
                </Typography>
            )}

            {method === "register" &&
                renderField("login-username-textfield", "Username", "text", AccountCircle, username, handleUsernameChange)}

            {renderField("login-email-textfield", "Email", "email", Email, email, handleEmailChange)}

            {renderField("login-password-textfield", "Password", "password", Key, password, handlePasswordChange)}

            {method === "register" && renderToSPrivacyLabel()}

            <CustomButton
                variant="contained"
                label={submitButtonName}
                type="submit"
                sx={{
                    width: "225px",
                    marginTop: "20px",
                    fontSize: "1.25em",
                    color: "white",
                }}
                disabled={loading || (method === "register" && !agreedToTerms)}
            />
        </Box>
    );
};

export default Form;
